import { useMemo } from 'react';
import { flatten } from 'lodash';
import { useInfiniteQuery } from 'react-query';
import { EMPLOYEES_TABS } from '../../constants';
import * as api from '../../services/api';
import useAccount from '../account/useAccount';
import useBranches from '../account/useBranches';

const useEmployeesInfinite = ({ activeTab, search, filters, branches, excludeIds, pageSize = 50 }) => {
  const { accountId } = useAccount();
  const { branchesOptions } = useBranches();
  const branchesIds = useMemo(() => branches ?? branchesOptions.map(({ value }) => value), [branches, branchesOptions]);
  const storeKey = [
    'employees',
    pageSize,
    accountId,
    EMPLOYEES_TABS[activeTab].statusFilter,
    search,
    branchesIds,
    filters,
    excludeIds
  ];

  const { isLoading, isIdle, data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    storeKey,
    ({ pageParam }) =>
      api.fetchEmployees({
        page: pageParam || 1,
        ...{
          pageSize,
          accountId,
          statusFilter: EMPLOYEES_TABS[activeTab].statusFilter,
          searchTerm: search,
          branches: branchesIds,
          filters,
          excludeIds
        }
      }),
    {
      enabled: branchesOptions.length > 0,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastGroup, allGroups) => {
        const totalItems = allGroups.reduce((sum, { data: nextData }) => sum + nextData.length, 0);
        return totalItems === lastGroup.total ? false : allGroups.length + 1;
      }
    }
  );

  const formattedData = useMemo(() => flatten((data?.pages || []).map(({ data: pageData }) => pageData)), [
    data?.pages
  ]);

  return {
    isLoading,
    isIdle,
    data: formattedData,
    fetchMore: fetchNextPage,
    canFetchMore: hasNextPage,
    isFetchingMore: isFetchingNextPage,
    totalItems: data?.pages?.[0]?.total
  };
};

export default useEmployeesInfinite;
